import { css, html, LitElement, nothing, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { i18n, TranslationKey } from '../../i18n/i18n';
import { ToastService } from '../../services/luds-toast-service';
import resetStyle from '../../shared/components/html-content/reset.style';
import { ludsSpacing } from '../../shared/styles/spacing';
import { ludsBaseFont, ludsTypography, ludsTypographyFontWeight } from '../../shared/styles/typography';
import { utilsClasses } from '../../shared/styles/utils';
import { ToastEvents, toastIconMap, ToastVariant, ToastVariants } from '../../shared/types/toast.model';
import '../button/button';
import '../icon/icon';

/**
 * Toast component
 */
@customElement('luds-toast')
export class LudsToast extends LitElement {
  /** Specifies the title of the toast */
  @property({ type: String })
  override title: string;

  /** Specifies the message content of the toast */
  @property({ type: String }) message: string | TemplateResult;

  /** Specifies the actions of the toast */
  @property({ type: Array }) actions?: { label: string; id: number }[];

  /** Specifies the variant of the toast. Valid values are 'success', 'error'. Default value is 'success' */
  @property({ type: String, reflect: true }) variant: ToastVariant = 'success';

  /** If true, the toast will have a close button. If false, the toast will not have a close button */
  @property({ type: Boolean }) closable = true;

  /** A11Y label for the close button of the toast component. */
  @property({ type: String }) a11yCloseLabel: TranslationKey = 'lua.toast.button.close';

  /** Callback function to be called when the toast is closed */
  @property({ attribute: false }) onClose?: () => void;

  /** Index of the toast */
  @property({ type: Number }) indexId: number;

  static override styles = [
    resetStyle,
    ludsBaseFont,
    ludsTypographyFontWeight,
    ludsTypography,
    ludsSpacing,
    utilsClasses,
    css`
      :host {
        --toast-position: var(--spacing-spacing-16);
        --toast-width: 360px;
        --toast-border-radius: 4px;

        width: var(--toast-width);
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        top: var(--toast-position);
        right: var(--toast-position);
        background-color: var(--semantic-colour-tokens-surface-surface-dark);
        color: var(--semantic-colour-tokens-text-text-on-dark-surface);
        fill: var(--colours-neutrals-white-negative);
        padding: var(--spacing-spacing-16);
        border-radius: var(--toast-border-radius);
        overflow: hidden;

        transform: translateX(110%);
        transition:
          transform 0.3s ease-out,
          opacity 0.3s ease-out;

        &::after {
          content: '';
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          width: 6px;
        }
      }

      :host([variant='success']) {
        & luds-icon {
          fill: var(--semantic-colour-tokens-message-message-success-background);
        }

        &::after {
          background: var(--semantic-colour-tokens-message-message-success-indicator);
        }
      }

      :host([variant='error']) {
        & luds-icon {
          fill: var(--semantic-colour-tokens-message-message-error-background);
        }

        &::after {
          background: var(--semantic-colour-tokens-message-message-error-indicator);
        }
      }

      .toast {
        width: 100%;
        display: flex;
        align-items: flex-start;
      }
    `,
  ];

  protected override firstUpdated(): void {
    this.show();
  }

  protected override updated(): void {
    this.updatePosition();
  }

  show() {
    requestIdleCallback(() => {
      this.style.transform = `translateX(0)`;
    });

    if (this.variant === ToastVariants.SUCCESS) {
      this.requestUpdate();
    }
  }

  hide() {
    this.style.opacity = '0';
    this.style.transform = 'translateX(110%)';

    // dismiss on transition end
    this.addEventListener(
      'transitionend',
      () => {
        ToastService.getInstance().dismissToast(this.indexId);
      },
      { once: true }
    );
  }

  /**
   * Updates the position of the toast and schedules it to hide after a delay if the variant is 'success'.
   */
  updatePosition() {
    const hideDelay = 8000; // Delay in milliseconds before the toast is hidden

    if (this.variant === ToastVariants.SUCCESS) {
      setTimeout(() => {
        this.hide();
      }, hideDelay);
    }
  }

  override render() {
    const { title, message, closable, variant, actions } = this;

    const toastClasses = {
      toast: true,
    };

    const titleClasses = {
      'p-default': true,
      'fw-600': true,
      'mt-0': true,
      'mb-4': !!message,
    };

    const messageClasses = {
      'p-default': true,
      'fw-400': true,
      'mt-0': true,
      'mb-12': !!actions,
    };

    const contentWrapperClasses = {
      'w-100': true,
      'ml-12': true,
      'mr-12': !!closable,
    };

    return html` <div class="${classMap(toastClasses)}" role="alert" aria-live="assertive" aria-atomic="true">
        <luds-icon icon="${ifDefined(toastIconMap.get(variant))}" size="large" aria-hidden="true"></luds-icon>
        <div class="${classMap(contentWrapperClasses)}">
          <p class="${classMap(titleClasses)}">${title}</p>
          <p class="${classMap(messageClasses)}">${message}</p>
          ${actions
            ? actions.map(
                (action) =>
                  html`<luds-button
                    class="${actions.length > 1 ? 'mr-12' : ''}"
                    variant="secondary-inverse"
                    text="${action.label}"
                    size="extra-small"
                    @click="${() => this.onActionClicked(action.id)}"></luds-button>`
              )
            : nothing}
        </div>
      </div>
      ${closable
        ? html`<luds-button
            variant="ghost-inverse"
            icon="close"
            size="large"
            a11yAriaLabel="${i18n(this.a11yCloseLabel)}"
            @click="${this.hide}"></luds-button>`
        : nothing}`;
  }

  private onActionClicked(actionID: number) {
    const event = new CustomEvent(ToastEvents.ACTION_CLICK, {
      detail: { actionID },
      bubbles: true,
      composed: true,
    });

    this.dispatchEvent(event);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'luds-toast': LudsToast;
  }
}
